import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Inbox, CheckCircle2, HelpCircle, EyeOff, Clock3, AlertCircle } from "lucide-react";
import { api } from "@/lib/api";

/**
 * Dashboard-header card: reply-by-email loop health, last 24h.
 *
 * Four counters — pulled / matched / unmatched / dismissed — + the
 * last-poll watermark relative to now. Clickable, routes to the
 * Inbound Inbox screen. Degrades gracefully to a "needs setup" chip
 * when inbound polling isn't enabled on the tenant.
 */
export default function InboundHealthCard() {
  const [stats, setStats] = useState(null);
  const [err, setErr] = useState(null);

  async function load() {
    try {
      const { data } = await api.get("/commissions/inbound/stats");
      setStats(data);
      setErr(null);
    } catch (e) {
      setErr(e.response?.data?.detail || "stats unavailable");
    }
  }
  useEffect(() => {
    load();
    const t = setInterval(load, 60000); // tick every minute
    const onFocus = () => load();
    window.addEventListener("focus", onFocus);
    return () => { clearInterval(t); window.removeEventListener("focus", onFocus); };
  }, []);

  if (err) return null;
  if (!stats) {
    return (
      <div
        className="rounded-xl border border-slate-800 bg-slate-900/60 px-4 py-3 text-xs text-slate-500 inline-flex items-center gap-2"
        data-testid="inbound-health-card-loading"
      >
        <Inbox className="w-3.5 h-3.5 animate-pulse" /> Loading inbound…
      </div>
    );
  }

  // Needs setup — surface a dim chip pointing at Settings.
  if (!stats.inbound_enabled || !stats.inbound_mailbox) {
    return (
      <Link
        to="/admin/settings"
        className="rounded-xl border border-slate-800 bg-slate-900/60 hover:border-amber-500/40 hover:bg-slate-900 px-4 py-3 inline-flex items-center gap-3 transition-colors"
        data-testid="inbound-health-card-setup"
        title="Reply-by-email isn't enabled. Click to configure the inbound mailbox in Settings."
      >
        <AlertCircle className="w-4 h-4 text-amber-400" />
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-widest text-amber-300 font-semibold">Inbound email</div>
          <div className="text-xs text-slate-400 mt-0.5">Not configured · set up in Settings</div>
        </div>
      </Link>
    );
  }

  const freshness = _relativeMinutes(stats.last_polled_at);
  // Red if we haven't polled in >5min (expected cadence is 60s).
  const stale = freshness !== null && freshness > 5;

  return (
    <Link
      to="/admin/inbound-inbox"
      className="group rounded-xl border border-slate-800 bg-slate-900/60 hover:border-cyan-500/40 hover:bg-slate-900 px-4 py-3 inline-flex items-start gap-4 transition-colors"
      data-testid="inbound-health-card"
      title={`Reply-by-email · ${stats.inbound_mailbox}\nLast poll: ${_absolute(stats.last_polled_at)}\nClick to open Inbound Inbox`}
    >
      <Inbox className={`w-4 h-4 mt-0.5 ${stale ? "text-amber-400" : "text-cyan-400"} group-hover:scale-110 transition-transform`} />
      <div>
        <div className="text-[10px] uppercase tracking-widest font-semibold inline-flex items-center gap-2">
          <span className={stale ? "text-amber-300" : "text-cyan-300"}>Inbound email · 24h</span>
          <span className={`inline-flex items-center gap-1 text-[9px] normal-case tracking-normal ${stale ? "text-amber-400" : "text-slate-500"}`}>
            <Clock3 className="w-2.5 h-2.5" />
            {freshness === null ? "awaiting first tick" :
             freshness < 1 ? "just now" :
             `${freshness}m ago`}
          </span>
        </div>
        <div className="flex items-center gap-4 mt-1.5 text-xs">
          <Chip icon={<Inbox className="w-3 h-3" />} label="pulled"
                value={stats.pulled_24h} tone="slate" testid="inbound-pulled" />
          <Chip icon={<CheckCircle2 className="w-3 h-3" />} label="matched"
                value={stats.matched_24h} tone="emerald" testid="inbound-matched" />
          <Chip icon={<HelpCircle className="w-3 h-3" />} label="unmatched"
                value={stats.unmatched_24h}
                tone={stats.unmatched_24h > 0 ? "amber" : "slate"}
                testid="inbound-unmatched" />
          <Chip icon={<EyeOff className="w-3 h-3" />} label="dismissed"
                value={stats.dismissed_24h} tone="slate" testid="inbound-dismissed" />
        </div>
      </div>
    </Link>
  );
}

function Chip({ icon, label, value, tone, testid }) {
  const toneCls = {
    emerald: "text-emerald-300",
    amber: "text-amber-300",
    slate: "text-slate-300",
  }[tone] || "text-slate-300";
  return (
    <div className="inline-flex items-center gap-1.5" data-testid={testid}>
      <span className={toneCls}>{icon}</span>
      <span className={`font-mono font-semibold tabular-nums ${toneCls}`}>{value}</span>
      <span className="text-[10px] text-slate-500">{label}</span>
    </div>
  );
}

function _relativeMinutes(iso) {
  if (!iso) return null;
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return null;
  return Math.max(0, Math.round((Date.now() - t) / 60000));
}
function _absolute(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
}
